import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";
import { EnvelopeSimple, ArrowRight, CheckCircle } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Something went wrong.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 border-r border-white/10 aura-grid">
        <Link to="/" className="flex items-center gap-3" data-testid="forgot-brand">
          <Logo size={44} />
        </Link>
        <div>
          <h2 className="heading-font text-4xl font-black tracking-tighter leading-tight">
            Reset your<br />cryptographic access.
          </h2>
          <p className="mono-font text-xs text-white/50 mt-4 max-w-md leading-relaxed">
            Enter your email and we'll send a signed reset link. Valid for 1 hour, single-use.
          </p>
        </div>
        <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">
          © 2026 Zayne Crouse · Patent DTTM-001
        </div>
      </div>
      <div className="flex items-center justify-center p-6 md:p-16">
        <div className="w-full max-w-md">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3">Session · Forgot</div>
          <h1 className="heading-font text-3xl font-black tracking-tighter">Forgot password?</h1>
          <p className="text-white/50 text-sm mt-2">We'll send you a reset link.</p>

          {sent ? (
            <div className="mt-10 border border-[#00FF66]/40 p-6" data-testid="forgot-sent">
              <div className="flex items-center gap-3">
                <CheckCircle size={22} weight="fill" className="text-[#00FF66]" />
                <div className="heading-font text-lg font-bold">Check your inbox</div>
              </div>
              <p className="text-white/70 text-sm mt-3">
                If an account exists for <span className="text-[#00FF66] mono-font">{email}</span>, you'll receive a reset link within a minute.
              </p>
              <Link to="/login" className="mt-6 inline-flex items-center gap-2 mono-font text-xs uppercase tracking-[0.25em] text-white/60 hover:text-white transition-colors duration-200">
                ← Back to login
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4">
              <div>
                <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Email</label>
                <div className="mt-2 flex items-center border border-white/15 focus-within:border-[#00FF66] transition-colors duration-200">
                  <div className="pl-3 text-white/40"><EnvelopeSimple size={16} /></div>
                  <input
                    data-testid="forgot-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent px-3 py-3 mono-font text-sm text-white focus:outline-none"
                    placeholder="you@aura.io"
                  />
                </div>
              </div>
              {error && (
                <div data-testid="forgot-error" className="mono-font text-xs text-[#FF0033] border border-[#FF0033]/40 px-3 py-2">
                  {error}
                </div>
              )}
              <button
                type="submit"
                data-testid="forgot-submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-3 px-5 py-3 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Reset Link"}
                <ArrowRight weight="bold" size={14} />
              </button>
            </form>
          )}

          <div className="mt-8 mono-font text-xs text-white/50">
            Remembered it?{" "}
            <Link to="/login" data-testid="forgot-to-login" className="text-[#00FF66] hover:text-white transition-colors duration-200">
              Log in →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
